import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Smartphone, Zap, WifiOff, BellRing, X, Share, Check } from "lucide-react";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}
function isMobile() {
  return /iphone|ipad|ipod|android/i.test(window.navigator.userAgent) || window.innerWidth < 768;
}

export function InstallApp() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setGuide(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      setGuide(true);
    }
  };

  const benefits = [
    { icon: Zap, label: t("install.b1") },
    { icon: WifiOff, label: t("install.b2") },
    { icon: BellRing, label: t("install.b3") },
  ];

  const steps = isIOS()
    ? { title: t("install.iosTitle"), items: [t("install.iosStep1"), t("install.iosStep2"), t("install.iosStep3")] }
    : { title: t("install.androidTitle"), items: [t("install.androidStep1"), t("install.androidStep2"), t("install.androidStep3")] };

  return (
    <section id="install" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="install-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-3xl bg-white border border-[#EFE7DC] shadow-[0_8px_30px_rgba(11,19,32,0.06)] p-5 sm:p-7 flex flex-col sm:flex-row items-center gap-5 sm:gap-7"
      >
        <img src="/brand-logo.png" alt="Mboa Go" className="h-12 w-auto shrink-0 object-contain" />
        <div className="flex-1 text-center sm:text-left">
          <p className="text-[#F26419] text-xs font-semibold uppercase tracking-[0.12em]">{t("install.kicker")}</p>
          <h2 className="mt-1 font-head font-black text-xl sm:text-2xl tracking-tight text-[#1A1A1A]">{t("install.secTitle")}</h2>
          <p className="mt-1.5 text-[#75807A] text-sm max-w-md leading-relaxed">{t("install.secText")}</p>
          <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-2">
            {benefits.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF3E8] border border-[#F3DFC9] px-3 py-1 text-[#3B423E] text-xs font-medium">
                <b.icon className="w-3.5 h-3.5 text-[#F26419]" /> {b.label}
              </span>
            ))}
          </div>
        </div>
        <div className="shrink-0">
          {installed ? (
            <div data-testid="install-done" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#008955] text-white font-bold">
              <Check className="w-5 h-5" /> {t("install.installedOk")}
            </div>
          ) : (
            <button
              data-testid="install-app-btn"
              onClick={handleInstall}
              className="group h-12 px-6 rounded-full bg-[#F26419] hover:bg-[#D85312] text-white font-bold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5 shadow-lg shadow-[#F26419]/25"
            >
              <Download className="w-5 h-5" /> {t("install.installNow")}
            </button>
          )}
        </div>
      </motion.div>

      {/* Guided steps modal */}
      <AnimatePresence>
        {guide && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setGuide(false)}
            data-testid="install-guide-modal"
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 320 }}
              className="bg-white rounded-3xl w-full max-w-md p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="install-guide-close"
                onClick={() => setGuide(false)}
                className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-[#F0EBE1] flex items-center justify-center"
              >
                <X className="w-5 h-5 text-[#75807A]" />
              </button>
              <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
                {isIOS() ? <Share className="w-6 h-6 text-white" /> : <Smartphone className="w-6 h-6 text-white" />}
              </span>
              <h3 className="mt-4 font-head font-bold text-xl text-[#1A1A1A]">{steps.title}</h3>
              <ol className="mt-5 space-y-4">
                {steps.items.map((s, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[#F26419] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="text-[#3B423E] leading-relaxed pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
              <button
                data-testid="install-guide-gotit"
                onClick={() => setGuide(false)}
                className="mt-7 w-full py-3.5 rounded-2xl bg-[#1A1A1A] text-white font-bold"
              >
                {t("install.gotIt")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
